import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { hasCapability } from "../lib/capabilities";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Package, Plus, Search, Building, Trash2, Clock, CheckCircle2, AlertCircle, Send, Trophy } from "lucide-react";
import MaterialRealityBoard from "../features/materials/MaterialRealityBoard";
import { useMaterialReality } from "../features/materials/useMaterialReality";
import { filterMaterials, orderMaterials } from "../features/materials/materialPresentation";
import FieldCaptureSheet from "../features/runtime/FieldCaptureSheet";
import { fieldObservationApi } from "../services/intelligenceService";

const BID_STATUSES = ["upcoming", "pending", "submitted", "awarded", "missed"];
const ITEM_STATUSES = ["planned", "quoted", "ordered", "shipped", "delivered", "received", "inspected", "staged", "canceled"];

const ProcurementPage = () => {
  const navigate = useNavigate();
  const { user, capabilities } = useAuth();
  // Awarding (including the awarded status transition) is capability-gated
  // backend-side (award.authorize); mirror the static floor here.
  const canAward = hasCapability(capabilities, "award.authorize");
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFilter = searchParams.get("project_id") || "";
  const [activeTab, setActiveTab] = useState("bids");
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [bids, setBids] = useState([]);
  const [procurementItems, setProcurementItems] = useState([]);
  const [summary, setSummary] = useState({ bids: {}, items: {}, upcoming_deadlines: [] });
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isBidDialogOpen, setIsBidDialogOpen] = useState(false);
  const [awardingBidId, setAwardingBidId] = useState(null);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [isVendorDialogOpen, setIsVendorDialogOpen] = useState(false);

  /* ---------------------------------------------------------------------
   * MATERIAL REALITY — the field's half of procurement.
   *
   * Project-scoped, because delivery authority is: `material-authority-1`
   * resolves one project's materials and there is no tenant-wide read. With
   * no project chosen the tab says so rather than showing an empty board,
   * which would read as "this project has no materials".
   * ------------------------------------------------------------------ */
  const materialReality = useMaterialReality(projectFilter || null);
  const [materialQuery, setMaterialQuery] = useState("");
  const [materialStatusFilter, setMaterialStatusFilter] = useState("all");
  const [recordingMaterial, setRecordingMaterial] = useState(null);

  /* The pending capture belongs to the project it was opened on. Switching
     projects closes it rather than letting it submit an observation against
     a material the user is no longer looking at. */
  useEffect(() => {
    setRecordingMaterial(null);
  }, [projectFilter]);

  const visibleMaterials = orderMaterials(
    filterMaterials(materialReality.materials, {
      query: materialQuery,
      status: materialStatusFilter,
    })
  );

  const [bidForm, setBidForm] = useState({
    title: "",
    project_id: "",
    vendor_id: "",
    trade: "",
    status: "upcoming",
    due_date: "",
    estimated_value: 0,
    source: "",
    notes: "",
  });
  const [itemForm, setItemForm] = useState({
    project_id: "",
    bid_package_id: "",
    name: "",
    category: "",
    quantity: 1,
    unit_of_measure: "EA",
    unit_cost_estimated: 0,
    lead_time_days_estimated: 0,
    vendor_id: "",
    status: "planned",
  });
  const [vendorForm, setVendorForm] = useState({
    name: "",
    vendor_type: "supplier",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, vendorsRes, bidsRes, itemsRes, summaryRes, comparisonRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/bids`),
        axios.get(`${API}/procurement`),
        axios.get(`${API}/procurement/summary`),
        axios.get(`${API}/bids/comparison`),
      ]);
      setProjects(projectsRes.data || []);
      setVendors(vendorsRes.data || []);
      setBids(bidsRes.data || []);
      setProcurementItems(itemsRes.data || []);
      setSummary(summaryRes.data || { bids: {}, items: {}, upcoming_deadlines: [] });
      setComparisons(comparisonRes.data || []);
    } catch (error) {
      toast.error("Failed to load bid/procurement data");
    } finally {
      setLoading(false);
    }
  };

  const cleanPayload = (payload) =>
    Object.fromEntries(Object.entries(payload).map(([k, v]) => [k, v === "" ? null : v]));

  const handleCreateBid = async (e) => {
    e.preventDefault();
    try {
      const payload = cleanPayload({ ...bidForm, due_date: bidForm.due_date || null });
      const response = await axios.post(`${API}/bids`, payload);
      setBids([response.data, ...bids]);
      setIsBidDialogOpen(false);
      setBidForm({ title: "", project_id: "", vendor_id: "", trade: "", status: "upcoming", due_date: "", estimated_value: 0, source: "", notes: "" });
      toast.success("Bid package created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create bid");
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/procurement`, cleanPayload(itemForm));
      setProcurementItems([response.data, ...procurementItems]);
      setIsItemDialogOpen(false);
      setItemForm({ project_id: "", bid_package_id: "", name: "", category: "", quantity: 1, unit_of_measure: "EA", unit_cost_estimated: 0, lead_time_days_estimated: 0, vendor_id: "", status: "planned" });
      toast.success("Procurement item created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create item");
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/vendors`, cleanPayload(vendorForm));
      setVendors([response.data, ...vendors]);
      setIsVendorDialogOpen(false);
      setVendorForm({ name: "", vendor_type: "supplier", contact_name: "", contact_email: "", contact_phone: "" });
      toast.success("Vendor created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create vendor");
    }
  };

  const awardBid = async (bidId, createProcurementItem = true) => {
    if (awardingBidId) return; // one award POST at a time — no double-click double-award
    setAwardingBidId(bidId);
    try {
      await axios.post(`${API}/bids/${bidId}/award`, {
        create_procurement_item: createProcurementItem,
        mark_competing_missed: true,
      });
      await fetchData();
      toast.success("Bid awarded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to award bid");
    } finally {
      setAwardingBidId(null);
    }
  };

  const updateBidStatus = async (bidId, status) => {
    try {
      const response = await axios.put(`${API}/bids/${bidId}`, { status });
      setBids(bids.map((bid) => (bid.id === bidId ? response.data : bid)));
      fetchData();
      toast.success("Bid status updated");
    } catch {
      toast.error("Failed to update bid status");
    }
  };

  const updateItemStatus = async (itemId, status) => {
    try {
      const response = await axios.put(`${API}/procurement/${itemId}`, { status });
      setProcurementItems(procurementItems.map((item) => (item.id === itemId ? response.data : item)));
      toast.success("Item status updated");
    } catch {
      toast.error("Failed to update item status");
    }
  };

  const deleteBid = async (bidId) => {
    if (!window.confirm("Delete this bid package?")) return;
    await axios.delete(`${API}/bids/${bidId}`);
    setBids(bids.filter((bid) => bid.id !== bidId));
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this procurement item?")) return;
    await axios.delete(`${API}/procurement/${itemId}`);
    setProcurementItems(procurementItems.filter((item) => item.id !== itemId));
  };

  const formatCurrency = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "No date");

  const filteredBids = bids.filter((bid) =>
    (!projectFilter || bid.project_id === projectFilter) &&
    [bid.title, bid.project_name, bid.vendor_name, bid.trade, bid.status].some((value) => (value || "").toLowerCase().includes(searchQuery.toLowerCase()))
  );
  const filteredItems = procurementItems.filter((item) =>
    (!projectFilter || item.project_id === projectFilter) &&
    [item.name, item.project_name, item.vendor_name, item.category, item.status].some((value) => (value || "").toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const statusClass = (status) => ({
    upcoming: "bg-blue-500/20 text-blue-400",
    pending: "bg-amber-500/20 text-amber-400",
    submitted: "bg-primary/20 text-primary",
    awarded: "bg-emerald-500/20 text-emerald-400",
    missed: "bg-red-500/20 text-red-400",
    planned: "bg-muted text-muted-foreground",
    ordered: "bg-amber-500/20 text-amber-400",
    delivered: "bg-primary/20 text-primary",
    received: "bg-emerald-500/20 text-emerald-400",
  }[status] || "bg-muted text-muted-foreground");

  if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-pulse text-primary text-xl">Loading bid/procurement tracker...</div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="procurement-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Bid & Procurement Tracker</h1>
          <p className="text-muted-foreground mt-1">Track bid packages, vendors, material needs, deadlines, and award status.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isVendorDialogOpen} onOpenChange={setIsVendorDialogOpen}>
            <DialogTrigger asChild><Button variant="outline"><Building className="mr-2 h-4 w-4" />New Vendor</Button></DialogTrigger>
            <DialogContent className="bg-card"><DialogHeader><DialogTitle>Create Vendor/Subcontractor</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateVendor} className="space-y-4 mt-4">
                <Input placeholder="Company name" value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} required />
                <Select value={vendorForm.vendor_type} onValueChange={(value) => setVendorForm({ ...vendorForm, vendor_type: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["supplier", "subcontractor", "manufacturer", "consultant", "other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                <Input placeholder="Contact name" value={vendorForm.contact_name} onChange={(e) => setVendorForm({ ...vendorForm, contact_name: e.target.value })} />
                <Input placeholder="Contact email" value={vendorForm.contact_email} onChange={(e) => setVendorForm({ ...vendorForm, contact_email: e.target.value })} />
                <Input placeholder="Contact phone" value={vendorForm.contact_phone} onChange={(e) => setVendorForm({ ...vendorForm, contact_phone: e.target.value })} />
                <Button type="submit" className="w-full">Create Vendor</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={isBidDialogOpen} onOpenChange={setIsBidDialogOpen}>
            <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />New Bid</Button></DialogTrigger>
            <DialogContent className="bg-card"><DialogHeader><DialogTitle>Create Bid Package</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateBid} className="space-y-4 mt-4">
                <Input placeholder="Bid title / scope package" value={bidForm.title} onChange={(e) => setBidForm({ ...bidForm, title: e.target.value })} required />
                <Select value={bidForm.project_id || undefined} onValueChange={(value) => setBidForm({ ...bidForm, project_id: value })}><SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger><SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
                <Select value={bidForm.vendor_id || undefined} onValueChange={(value) => setBidForm({ ...bidForm, vendor_id: value })}><SelectTrigger><SelectValue placeholder="Vendor/Subcontractor" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select>
                <Input placeholder="Trade (electrical, concrete, roofing...)" value={bidForm.trade} onChange={(e) => setBidForm({ ...bidForm, trade: e.target.value })} />
                <div className="grid grid-cols-2 gap-3"><div><Label>Due Date</Label><Input type="datetime-local" value={bidForm.due_date} onChange={(e) => setBidForm({ ...bidForm, due_date: e.target.value })} /></div><div><Label>Estimated Value</Label><Input type="number" value={bidForm.estimated_value} onChange={(e) => setBidForm({ ...bidForm, estimated_value: Number(e.target.value) })} /></div></div>
                <Button type="submit" className="w-full">Create Bid</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={isItemDialogOpen} onOpenChange={setIsItemDialogOpen}>
            <DialogTrigger asChild><Button variant="outline"><Package className="mr-2 h-4 w-4" />New Item</Button></DialogTrigger>
            <DialogContent className="bg-card"><DialogHeader><DialogTitle>Create Procurement Item</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateItem} className="space-y-4 mt-4">
                <Select value={itemForm.project_id || undefined} onValueChange={(value) => setItemForm({ ...itemForm, project_id: value })}><SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger><SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
                <Input placeholder="Item name" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} required />
                <Select value={itemForm.vendor_id || undefined} onValueChange={(value) => setItemForm({ ...itemForm, vendor_id: value })}><SelectTrigger><SelectValue placeholder="Vendor" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select>
                <div className="grid grid-cols-3 gap-3"><Input type="number" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: Number(e.target.value) })} /><Input value={itemForm.unit_of_measure} onChange={(e) => setItemForm({ ...itemForm, unit_of_measure: e.target.value })} /><Input type="number" value={itemForm.unit_cost_estimated} onChange={(e) => setItemForm({ ...itemForm, unit_cost_estimated: Number(e.target.value) })} /></div>
                <Button type="submit" className="w-full">Create Item</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat icon={Clock} label="Upcoming" value={summary.bids?.upcoming ?? bids.filter(b => b.status === "upcoming").length} />
        <Stat icon={AlertCircle} label="Pending" value={summary.bids?.pending ?? bids.filter(b => b.status === "pending").length} />
        <Stat icon={Send} label="Submitted" value={summary.bids?.submitted ?? bids.filter(b => b.status === "submitted").length} />
        <Stat icon={Trophy} label="Awarded" value={summary.bids?.awarded ?? bids.filter(b => b.status === "awarded").length} />
        <Stat icon={Package} label="Procurement Items" value={Object.values(summary.items || {}).reduce((sum, value) => sum + value, 0) || procurementItems.length} />
      </div>

      {(summary.upcoming_deadlines || []).length > 0 && (
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-amber-400" />
              <h2 className="font-heading font-semibold">Upcoming Bid Deadlines</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.upcoming_deadlines.map((bid) => (
                <div key={bid.id} className="rounded-lg bg-muted/30 p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{bid.title}</p>
                    <p className="text-xs text-muted-foreground">{bid.project_name || "Unassigned"} • {bid.vendor_name || "No vendor"}</p>
                  </div>
                  <span className="text-xs text-amber-400">{formatDate(bid.due_date)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search bids, projects, vendors, trades, or items..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-muted/50" />
        </div>
        {projectFilter && (
          <Button variant="outline" size="sm" onClick={() => setSearchParams({})}>
            Project: {projects.find((p) => p.id === projectFilter)?.name || projectFilter} ✕
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto"><TabsTrigger value="bids">Bid Packages</TabsTrigger><TabsTrigger value="comparison">Compare &amp; Award</TabsTrigger><TabsTrigger value="items">Procurement Items</TabsTrigger><TabsTrigger value="materials" data-testid="tab-materials">Material Reality</TabsTrigger><TabsTrigger value="vendors">Vendors</TabsTrigger></TabsList>
        <TabsContent value="bids" className="space-y-3 mt-4">
          {filteredBids.map((bid) => <Card key={bid.id} className="stat-card"><CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-4 justify-between"><div><div className="flex items-center gap-2"><h3 className="font-semibold">{bid.title}</h3><span className={`status-badge ${statusClass(bid.status)}`}>{bid.status}</span></div><p className="text-sm text-muted-foreground mt-1">{bid.project_name || "No project"} • {bid.vendor_name || "No vendor"} • {bid.trade || "No trade"}</p><p className="text-sm mt-1">Due {formatDate(bid.due_date)} • {formatCurrency(bid.estimated_value)}</p></div><div className="flex gap-2 items-center"><Button size="sm" variant="outline" onClick={() => navigate(`/procurement/packages/${bid.id}`)}><Package className="mr-2 h-4 w-4" />Workspace</Button><Select value={bid.status} onValueChange={(value) => updateBidStatus(bid.id, value)}><SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger><SelectContent>{BID_STATUSES.map(s => <SelectItem key={s} value={s} disabled={s === "awarded" && !canAward}>{s}</SelectItem>)}</SelectContent></Select>{canAward && <Button size="sm" variant="outline" disabled={bid.status === "awarded" || awardingBidId !== null} onClick={() => awardBid(bid.id, true)}><Trophy className="mr-2 h-4 w-4" />Award</Button>}<Button variant="ghost" size="icon" onClick={() => deleteBid(bid.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></div></CardContent></Card>)}
          {filteredBids.length === 0 && <Empty label="No bid packages yet" />}
        </TabsContent>
        <TabsContent value="comparison" className="space-y-4 mt-4">
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardContent className="py-3 px-4 text-xs text-muted-foreground">
              This tab compares single estimated values. For like-for-like leveling
              (exclusions, scope gaps, qualification) and the EITL award decision, open a
              package's <span className="text-foreground font-medium">Workspace</span> — a
              one-click award here records no evaluation rationale.
            </CardContent>
          </Card>
          {comparisons.map((group) => (
            <Card key={`${group.project_id}-${group.trade}`} className="stat-card">
              <CardContent className="p-4 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">{group.trade}</h3>
                    <p className="text-sm text-muted-foreground">{group.project_name} • {group.bids.length} bid{group.bids.length === 1 ? "" : "s"}</p>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Spread: <span className="font-mono text-foreground">{formatCurrency(group.spread)}</span>
                  </div>
                </div>
                <div className="grid gap-3">
                  {group.bids.map((bid) => (
                    <div key={bid.id} className={`rounded-lg border p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3 ${bid.id === group.lowest_bid_id ? "border-emerald-500/50 bg-emerald-500/5" : "border-border bg-muted/20"}`}>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{bid.title}</p>
                          <span className={`status-badge ${statusClass(bid.status)}`}>{bid.status}</span>
                          {bid.id === group.lowest_bid_id && <span className="status-badge bg-emerald-500/20 text-emerald-400">lowest</span>}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{bid.vendor_name || "No vendor"} • Due {formatDate(bid.due_date)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-semibold">{formatCurrency(bid.estimated_value)}</span>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/procurement/packages/${bid.id}`)}>
                          <Package className="mr-2 h-4 w-4" />
                          Workspace
                        </Button>
                        {canAward && (
                          <Button size="sm" disabled={bid.status === "awarded" || awardingBidId !== null} onClick={() => awardBid(bid.id, true)}>
                            <Trophy className="mr-2 h-4 w-4" />
                            {bid.status === "awarded" ? "Awarded" : "Award"}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          {comparisons.length === 0 && <Empty label="No comparable bid packages yet" />}
        </TabsContent>
        <TabsContent value="items" className="space-y-3 mt-4">
          {filteredItems.map((item) => <Card key={item.id} className="stat-card"><CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-4 justify-between"><div><div className="flex items-center gap-2"><h3 className="font-semibold">{item.name}</h3><span className={`status-badge ${statusClass(item.status)}`}>{item.status}</span></div><p className="text-sm text-muted-foreground mt-1">{item.project_name || "Project"} • {item.vendor_name || "No vendor"} • {item.category || "Uncategorized"}</p><p className="text-sm mt-1">{item.quantity} {item.unit_of_measure} • {formatCurrency(item.total_cost_estimated)}</p></div><div className="flex gap-2 items-center"><Select value={item.status} onValueChange={(value) => updateItemStatus(item.id, value)}><SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger><SelectContent>{ITEM_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select><Button variant="ghost" size="icon" onClick={() => deleteItem(item.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></div></CardContent></Card>)}
          {filteredItems.length === 0 && <Empty label="No procurement items yet" />}
        </TabsContent>
        <TabsContent value="materials" className="space-y-3 mt-4">
          {/*
            The board is PROJECT-SCOPED and this page is not, so it carries its
            own switcher. A native select rather than a styled one: it needs no
            radix primitive, it is keyboard-operable for free, and on a phone
            it opens the platform picker a field user already knows.

            Switching here changes a search param, which react-router applies
            WITHOUT a reload — so the board stays mounted while the project
            changes under it. That is exactly the window in which a slow
            response from the previous project could land, and it is why the
            load is sequenced and the rows are cached under their own project
            id.
          */}
          {projects.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="material-project" className="text-xs">
                Project
              </Label>
              <select
                id="material-project"
                data-testid="material-project-select"
                value={projectFilter}
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  if (e.target.value) next.set("project_id", e.target.value);
                  else next.delete("project_id");
                  setSearchParams(next);
                }}
                className="h-9 rounded-md border border-input bg-muted/50 px-2 text-sm"
              >
                <option value="">Choose a project…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!projectFilter ? (
            /* An honest precondition, not an empty board. */
            <div className="px-4 py-8 text-center" data-testid="materials-need-project">
              <p className="text-sm font-medium">Choose a project to see its material reality</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Delivery authority is resolved per project. Without one, OMNΛS has nothing to
                resolve — this is not an empty project.
              </p>
            </div>
          ) : (
            <MaterialRealityBoard
              materials={visibleMaterials}
              counts={materialReality.counts}
              unreadable={materialReality.unreadable}
              contractError={materialReality.contractError}
              loadError={materialReality.loadError}
              loading={materialReality.loading}
              truncated={materialReality.truncated}
              onRetry={materialReality.reload}
              onRecord={setRecordingMaterial}
              query={materialQuery}
              onQuery={setMaterialQuery}
              statusFilter={materialStatusFilter}
              onStatusFilter={setMaterialStatusFilter}
            />
          )}

          {/* The field's own act, against one material. Storage is never
              refused; the projection may be, and the sheet keeps the two
              apart. START_WORK is absent because it is an activity-instance
              command and has nothing to do with a delivery. */}
          <FieldCaptureSheet
            open={Boolean(recordingMaterial)}
            onOpenChange={(next) => {
              if (!next) setRecordingMaterial(null);
            }}
            target="material"
            targetId={recordingMaterial?.id}
            targetName={recordingMaterial?.nameLabel || ""}
            onRecord={async (body) => {
              const result = await fieldObservationApi.recordMaterial(recordingMaterial.id, body);
              /* Refresh the board so the new evidence is reflected in the
                 authority ladder. A refresh failure is NOT a recording
                 failure: the observation is already stored, so the result is
                 returned either way. */
              materialReality.reload();
              return result;
            }}
          />
        </TabsContent>
        <TabsContent value="vendors" className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {vendors.map((vendor) => <Card key={vendor.id} className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-2"><Building className="h-5 w-5 text-primary" /><h3 className="font-semibold">{vendor.name}</h3></div><p className="text-sm text-muted-foreground capitalize mt-1">{vendor.vendor_type}</p><p className="text-sm mt-2">{vendor.contact_name || "No contact"}</p><p className="text-sm text-muted-foreground">{vendor.contact_email || vendor.contact_phone || "No contact info"}</p></CardContent></Card>)}
          {vendors.length === 0 && <Empty label="No vendors yet" />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold">{value}</p><p className="text-sm text-muted-foreground">{label}</p></div></div></CardContent></Card>;
const Empty = ({ label }) => <Card className="stat-card"><CardContent className="p-10 text-center text-muted-foreground">{label}</CardContent></Card>;

export default ProcurementPage;
